import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { playWaterDropSound, showDesktopNotification, isAdminPushEnabled } from "@/lib/notifySound";
import { CheckCircle2, XCircle, Save, ThumbsUp, ThumbsDown, MailOpen, Sparkles, Trash2, Users, Wallet, IdCard, AlertTriangle, LogIn, IndianRupee, Camera, Archive, ArchiveRestore, Ban, RotateCcw, Wand2, Pencil, Hourglass, Skull, Ticket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DeleteAccountCard from "@/components/DeleteAccountCard";
import RescheduleControls from "@/components/RescheduleControls";
import { UsernamePrompt } from "@/components/UsernamePrompt";
import FeedbackViewer from "@/components/FeedbackViewer";
import EmployeeAdminPanel from "@/components/EmployeeAdminPanel";
import DeletedAccountsBin from "@/components/DeletedAccountsBin";
import AdminServiceDiscountPanel from "@/components/AdminServiceDiscountPanel";
import AdminBrandProfilePanel from "@/components/AdminBrandProfilePanel";
import AdminProfileEditsPanel from "@/components/AdminProfileEditsPanel";
import EditProfileModal from "@/components/EditProfileModal";
import PowerDeletedBin from "@/components/PowerDeletedBin";
import { PowerDeleteProvider, PowerDeleteButton, usePowerDelete } from "@/context/PowerDeleteContext";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QUALIFICATION_LABELS } from "@/pages/Apply";

const DAY_NAMES = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const TIER_LABEL = {
  pnb: "Pay Nothing Before",
  eea: "Earn Everything After",
};

function PowerDeleteToggleButton({ compact = false }) {
  const { active, setActive } = usePowerDelete();
  if (active) return null;
  return (
    <button data-testid={compact ? "power-delete-toggle-btn-mobile" : "power-delete-toggle-btn"}
      onClick={() => setActive(true)}
      className={`btn-outline inline-flex items-center justify-center gap-2 ${compact ? "w-full text-sm" : ""}`}
      style={{ borderColor: "#b91c1c", color: "#b91c1c" }}>
      <Skull className="w-4 h-4" /> Power Delete
    </button>
  );
}

export default function CounselorDashboard() {
  return (
    <PowerDeleteProvider>
      <CounselorDashboardInner />
    </PowerDeleteProvider>
  );
}

function CounselorDashboardInner() {
  const { user, setUser, refresh } = useAuth();
  const [availability, setAvailability] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [applications, setApplications] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState(null);
  const [kycQueue, setKycQueue] = useState([]);
  const [kycBusyId, setKycBusyId] = useState(null);
  const [clientCoupons, setClientCoupons] = useState({});
  const [couponInputs, setCouponInputs] = useState({});
  const [couponBusyId, setCouponBusyId] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const [layout, setLayout] = useState("list");
  const [activeTab, setActiveTab] = useState("bookings");
  const [pendingEdit, setPendingEdit] = useState(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const pendingDeleteTimers = useRef({});
  const nav = useNavigate();

  useEffect(() => {
    const timers = pendingDeleteTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const isLead = !!user?.is_lead;
  const seenEmployeeMsgIdsRef = useRef(null);

  useEffect(() => {
    if (!isLead) return;
    let cancelled = false;
    const MSG_TYPE_LABEL = { escalation: "Escalation", share: "Shared file", voice_note: "Voice message" };
    const poll = async () => {
      try {
        const { data } = await api.get("/admin/employee-messages/recent");
        if (cancelled) return;
        if (seenEmployeeMsgIdsRef.current === null) {
          seenEmployeeMsgIdsRef.current = new Set(data.map(d => d.id));
          return;
        }
        const fresh = data.filter(d => !seenEmployeeMsgIdsRef.current.has(d.id));
        fresh.forEach(d => {
          seenEmployeeMsgIdsRef.current.add(d.id);
          const label = MSG_TYPE_LABEL[d.type] || "message";
          toast.info(`${d.employee_name || "Employee"} — ${label}${d.summary ? `: ${d.summary}` : ""}`);
          playWaterDropSound();
          if (isAdminPushEnabled()) showDesktopNotification(`New ${label} from ${d.employee_name || "an employee"}`, d.summary || "");
        });
      } catch { /* ignore poll errors, retry next tick */ }
    };
    poll();
    const interval = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isLead]);
  const membershipStatus = user?.membership_status;
  const membershipTier = user?.membership_tier;
  const revenueShare = user?.counselor_revenue_share;
  const kycStatus = user?.kyc_status || "not_submitted";
  const membershipExpiry = user?.membership_expires_at
    ? new Date(user.membership_expires_at).toLocaleDateString()
    : null;

  // Force counselors to complete membership before using dashboard
  const needsMembership =
    !isLead && ["approved_awaiting_payment", "expired", "suspended"].includes(membershipStatus || "");

  const load = async () => {
    if (!user) return;
    const [a, b] = await Promise.all([
      api.get(`/availability/${user.id}`),
      api.get("/bookings", { params: isLead ? { include_archived: showArchived } : {} }),
    ]);
    setAvailability(a.data);
    setBookings(b.data);
    if (user.is_lead) {
      const [apps, tks, us, pays, kyc, dl, cc] = await Promise.all([
        api.get("/counselor-applications").catch(() => ({data: []})),
        api.get("/support-tickets").catch(() => ({data: []})),
        api.get("/users").catch(() => ({data: []})),
        api.get("/admin/payments").catch(() => ({data: null})),
        api.get("/admin/counselor-kyc").catch(() => ({data: {items: []}})),
        api.get("/admin/dashboard-layout").catch(() => ({data: {layout: "list"}})),
        api.get("/admin/client-coupons").catch(() => ({data: []})),
      ]);
      setApplications(apps.data || []);
      setTickets(tks.data || []);
      setUsers(us.data || []);
      setPayments(pays.data);
      setKycQueue(kyc.data?.items || []);
      setLayout(dl.data?.layout || "list");
      const ccMap = {};
      (cc.data || []).forEach(row => {
        if (!ccMap[row.client_id]) ccMap[row.client_id] = [];
        ccMap[row.client_id].push(row);
      });
      setClientCoupons(ccMap);
    } else if (user.role === "counselor") {
      const mine = await api.get("/counselor/profile-edit/mine").catch(() => ({ data: null }));
      setPendingEdit(mine.data && mine.data.status === "pending" ? mine.data : null);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [user, showArchived]);
  const { refreshToken } = usePowerDelete();
  useEffect(() => { if (user) load(); /* eslint-disable-line */ }, [refreshToken]);

  const changeLayout = async (next) => {
    setLayout(next);
    try { await api.patch("/admin/dashboard-layout", { layout: next }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const showSection = (key) => layout !== "grouped" || activeTab === key;

  // Keep bookings fresh so the auto-join banner surfaces new confirmations
  // and countdown stays accurate on long-idle counselor sessions.
  useEffect(() => {
    if (!user) return;
    const iv = setInterval(() => {
      api.get("/bookings")
        .then(r => setBookings(r.data))
        .catch(err => {
          if (err?.response?.status && err.response.status !== 401) {
            console.warn("Counselor bookings refresh failed:", err?.message || err);
          }
        });
    }, 60_000);
    return () => clearInterval(iv);
  }, [user]);

  if (needsMembership) return <Navigate to="/counselor/membership" replace />;

  const decideApp = async (a, decision) => {
    try {
      await api.patch(`/counselor-applications/${a.id}`, { decision });
      toast.success(
        decision === "approve"
          ? "Approved — counsellor picks a tier (Pay Nothing Before or Earn Everything After) on sign-in"
          : "Application rejected"
      );
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const closeTicket = async (id) => {
    try {
      await api.patch(`/support-tickets/${id}`);
      toast.success("Closed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? They'll move to the recovery bin and can be restored later.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const impersonateUser = async (u) => {
    try {
      await api.post(`/admin/impersonate/${u.id}`);
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      toast.success(`Now viewing as ${u.name}`);
      window.location.href = me.role === "counselor" ? "/counselor" : "/dashboard";
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const disableCounselor = async (u, duration) => {
    const label = { "1w": "1 week", "1m": "1 month", "1y": "1 year" }[duration] || duration;
    if (!window.confirm(`Disable ${u.name} for ${label}?\n\nThey won't receive new bookings. Existing bookings stay in place. They reactivate automatically when the window ends.`)) return;
    try {
      await api.post(`/admin/counselors/${u.id}/disable`, { duration });
      toast.success(`${u.name} disabled for ${label}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const enableCounselor = async (u) => {
    try {
      await api.post(`/admin/counselors/${u.id}/enable`);
      toast.success(`${u.name} re-enabled`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const attachCoupon = async (u) => {
    const code = (couponInputs[u.id] || "").trim();
    if (!code) { toast.error("Paste a coupon code first"); return; }
    setCouponBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/attach-coupon`, { code });
      toast.success(`Attached "${data.service_name}" coupon to ${u.name} — they've been emailed (expires ${new Date(data.expires_at).toLocaleDateString()})`);
      setCouponInputs(prev => ({ ...prev, [u.id]: "" }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCouponBusyId(null); }
  };

  const isCounselorDisabled = (u) => {
    if (!u?.disabled_until) return false;
    try { return new Date(u.disabled_until) > new Date(); } catch { return false; }
  };

  const refundBooking = async (b) => {
    const amount = b.price || b.amount || 0;
    if (!window.confirm(`Refund ₹${amount} to ${b.client_name} for "${b.service_name}"?\n\nThis issues a Razorpay refund and marks the booking as refunded. It cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/refund`);
      toast.success(`Refund initiated · ₹${data.amount}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reviewKyc = async (kyc, decision) => {
    if (kycBusyId) return;
    let reason = null;
    if (decision === "reject") {
      reason = window.prompt("Rejection reason (shown to counsellor):", "Please double-check your PAN and bank details.");
      if (reason === null) return;
    }
    setKycBusyId(kyc.user_id);
    try {
      const { data } = await api.post(`/admin/counselor-kyc/${kyc.user_id}/review`, { decision, reason });
      if (data.status === "verified") {
        if (data.razorpay_linked_account_id) {
          toast.success(`Verified · Razorpay linked account ${data.razorpay_linked_account_id}`);
        } else {
          toast.success(`Verified locally. Razorpay: ${data.razorpay_error || "Route not enabled yet"}`);
        }
      } else {
        toast.message("KYC rejected — counsellor has been notified.");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setKycBusyId(null);
    }
  };

  const toggleDay = (d) => {
    const cur = availability.days || [];
    const next = cur.includes(d) ? cur.filter(x=>x!==d) : [...cur,d].sort();
    setAvailability({ ...availability, days: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/availability", availability);
      toast.success("Availability updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/counselor/me/photo", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      // Merge the new photo_url into the current user so the avatar refreshes
      // everywhere it's rendered from the auth context.
      setUser(u => u ? { ...u, photo_url: data.photo_url } : u);
      toast.success("Profile photo updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPhotoBusy(false); }
  };

  const archiveBooking = async (id) => {
    setRowBusy(id);
    try {
      await api.post(`/admin/bookings/${id}/archive`);
      toast.success("Booking archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRowBusy(null); }
  };

  const unarchiveBooking = async (id) => {
    setRowBusy(id);
    try {
      await api.post(`/admin/bookings/${id}/unarchive`);
      toast.success("Booking restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRowBusy(null); }
  };

  const deleteBooking = (id) => {
    setBookings(prev => prev.map(b => b.id === id ? { ...b, _pendingDelete: true } : b));
    const timer = setTimeout(async () => {
      delete pendingDeleteTimers.current[id];
      try {
        await api.delete(`/admin/bookings/${id}?confirm=true`);
        setBookings(prev => prev.filter(b => b.id !== id));
      } catch (e) {
        toast.error(formatApiError(e));
        load();
      }
    }, 15000);
    pendingDeleteTimers.current[id] = timer;
    toast("Booking deleted", {
      description: "Payment and refund records will be preserved for accounting.",
      duration: 15000,
      action: {
        label: "Undo",
        onClick: () => {
          const t = pendingDeleteTimers.current[id];
          if (t) { clearTimeout(t); delete pendingDeleteTimers.current[id]; }
          setBookings(prev => prev.map(b => b.id === id ? { ...b, _pendingDelete: false } : b));
        },
      },
    });
  };

  const stats = {
    total: bookings.length,
    upcoming: bookings.filter(b => b.status === "confirmed").length,
    revenue: bookings.filter(b => b.payment_status === "paid").reduce((s,b)=>s+b.price, 0),
  };
  // Employees are managed entirely from the Employees tab now — keep this
  // list to clients/counselors only.
  const manageableUsers = users.filter(u => u.role !== "employee");

  if (!availability) return <div className="p-16 text-center">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {user && !user.username && <UsernamePrompt onSet={refresh} />}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="label-eyebrow">Counselor</span>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-2 mb-6">Your practice at a glance.</h1>
        </div>
        {isLead && (
          <div className="flex gap-3 w-full sm:w-auto">
            <div className="sm:hidden flex-1">
              <PowerDeleteToggleButton compact />
            </div>
            <div className="hidden sm:flex gap-3">
              <PowerDeleteToggleButton />
              <button data-testid="generate-counselor-btn"
                onClick={() => { sessionStorage.setItem("counseltaria_bypass_generate", "1"); nav("/"); }}
                className="btn-primary inline-flex items-center gap-2">
                <Wand2 className="w-4 h-4" /> Generate
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-4 mb-6" data-testid="profile-photo-block">
        <div
          className="w-16 h-16 rounded-full bg-[color:var(--surface)] border border-[color:var(--border-soft)] overflow-hidden flex-shrink-0"
          style={{
            backgroundImage: user?.photo_url ? `url(${user.photo_url})` : undefined,
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <label className="btn-outline text-xs inline-flex items-center gap-1 cursor-pointer"
               data-testid="upload-profile-photo">
          <Camera className="w-3.5 h-3.5"/>
          {photoBusy ? "Uploading…" : (user?.photo_url ? "Change photo" : "Add photo")}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                 className="hidden" disabled={photoBusy}
                 onChange={e => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }}/>
        </label>
        <button data-testid="edit-profile-btn" onClick={() => setShowEditProfile(true)}
          className="btn-outline text-xs inline-flex items-center gap-1">
          <Pencil className="w-3.5 h-3.5" /> Edit profile
        </button>
        {user?.admin_generated && (
          <span className="chip text-[0.6rem]" data-testid="admin-generated-tag"
            style={{ background: "rgba(230,178,58,0.18)", color: "#8a6a1f" }}>Admin-generated</span>
        )}
      </div>

      {pendingEdit && (
        <div className="rounded-2xl p-4 mb-6 flex items-center gap-2 text-sm"
             data-testid="pending-edit-banner"
             style={{ background: "rgba(232,176,92,0.18)", color: "#8a6a1f" }}>
          <Hourglass className="w-4 h-4 flex-shrink-0" />
          Your profile edit ({pendingEdit.proposed?.specialty}) is awaiting admin approval.
        </div>
      )}

      {!isLead && membershipStatus === "active" && (
        <div className="flex flex-wrap gap-3 items-center mb-8" data-testid="tier-banner">
          {membershipTier && (
            <span className="chip" style={{ background: "rgba(91,117,83,0.12)", color: "var(--brand)" }}>
              Tier · {TIER_LABEL[membershipTier] || membershipTier}
              {typeof revenueShare === "number" && ` · ${revenueShare}% payout`}
            </span>
          )}
          {membershipExpiry && (
            <span className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Renews on <strong>{membershipExpiry}</strong>. <Link to="/counselor/membership" className="underline" style={{ color: "var(--brand)" }}>Extend</Link>
            </span>
          )}
          {membershipTier === "pnb" && (
            <Link to="/counselor/membership" className="text-sm underline" style={{ color: "var(--brand)" }}>
              Upgrade to Earn Everything After
            </Link>
          )}
          <Link
            to="/counselor/kyc"
            data-testid="kyc-cta"
            className="chip text-[0.65rem]"
            style={{
              background: kycStatus === "verified" ? "rgba(91,117,83,0.15)"
                        : kycStatus === "rejected" ? "rgba(230,178,58,0.15)"
                        : "rgba(232,176,92,0.2)",
              color: kycStatus === "verified" ? "var(--brand)"
                   : kycStatus === "rejected" ? "var(--accent)" : "#8a6a1f",
            }}
          >
            <IdCard className="inline w-3 h-3 mr-1" />
            KYC · {kycStatus === "not_submitted" ? "not submitted" : kycStatus.replace("_", " ")}
          </Link>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        {[
          {label: "Total bookings", value: stats.total},
          {label: "Confirmed upcoming", value: stats.upcoming},
          {label: "Revenue (paid)", value: `₹${Math.round(stats.revenue)}`},
        ].map(s => (
          <div key={s.label} className="card-soft p-6">
            <div className="label-eyebrow">{s.label}</div>
            <div className="font-serif-display text-4xl mt-2">{s.value}</div>
          </div>
        ))}
      </div>

      {isLead && (
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8" data-testid="dashboard-layout-toggle">
          <div className="flex items-center gap-2">
            <span className="label-eyebrow mr-1">Layout</span>
            <button data-testid="layout-list-btn" onClick={() => changeLayout("list")}
              className={layout === "list" ? "btn-primary text-xs px-3 py-1.5" : "btn-outline text-xs px-3 py-1.5"}>List</button>
            <button data-testid="layout-grouped-btn" onClick={() => changeLayout("grouped")}
              className={layout === "grouped" ? "btn-primary text-xs px-3 py-1.5" : "btn-outline text-xs px-3 py-1.5"}>Grouped</button>
          </div>
          {layout === "grouped" && (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList data-testid="admin-dashboard-tabs">
                <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings & Availability</TabsTrigger>
                <TabsTrigger value="people" data-testid="admin-tab-people">People & Applications</TabsTrigger>
                <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
                <TabsTrigger value="employees" data-testid="admin-tab-employees">Employees</TabsTrigger>
                <TabsTrigger value="brand" data-testid="admin-tab-brand">Counseltaria Profile</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>
      )}

      {showSection("bookings") && (
      <div className="grid lg:grid-cols-5 gap-6 mb-12">
        <div className="card-soft p-8 lg:col-span-2">
          <h2 className="font-serif-display text-2xl mb-6">Weekly availability</h2>
          <div className="flex flex-wrap gap-2 mb-6">
            {DAY_NAMES.map((n, i) => (
              <button key={n} data-testid={`day-${i}`} onClick={() => toggleDay(i)}
                data-selected={availability.days.includes(i)}
                className="slot-btn">
                {n}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label-eyebrow block mb-2">Start</label>
              <input data-testid="avail-start" type="time" value={availability.start_time}
                onChange={e=>setAvailability({...availability, start_time: e.target.value})}
                className="w-full rounded-xl border border-[color:var(--border-soft)] px-3 py-2"/>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">End</label>
              <input data-testid="avail-end" type="time" value={availability.end_time}
                onChange={e=>setAvailability({...availability, end_time: e.target.value})}
                className="w-full rounded-xl border border-[color:var(--border-soft)] px-3 py-2"/>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Gap (min)</label>
              <input data-testid="avail-gap" type="number" min="0" max="60" value={availability.slot_gap_min}
                onChange={e=>setAvailability({...availability, slot_gap_min: Number(e.target.value)})}
                className="w-full rounded-xl border border-[color:var(--border-soft)] px-3 py-2"/>
            </div>
          </div>
          <button data-testid="save-availability" onClick={save} disabled={saving}
            className="btn-primary mt-6 inline-flex items-center gap-2">
            <Save className="w-4 h-4"/> {saving?"Saving…":"Save availability"}
          </button>
        </div>

        <div className="card-soft p-8 lg:col-span-3">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
            <h2 className="font-serif-display text-2xl">All bookings</h2>
            {isLead && (
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer"
                     style={{color:"var(--text-secondary)"}} data-testid="toggle-archived">
                <input type="checkbox" checked={showArchived}
                       onChange={e => setShowArchived(e.target.checked)}/>
                Show archived
              </label>
            )}
          </div>
          {bookings.length === 0 ? (
            <p style={{color:"var(--text-secondary)"}}>No bookings yet.</p>
          ) : (
            <div className="space-y-3" data-testid="all-bookings">
              {bookings.filter(b => !b._pendingDelete).map(b => (
                <div key={b.id} data-testid={`row-${b.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 border border-[color:var(--border-soft)] rounded-2xl p-4">
                  <div className="min-w-0">
                    <div className="font-medium">{b.client_name}</div>
                    <div className="text-xs" style={{color:"var(--text-secondary)"}}>{b.client_email}</div>
                    <div className="text-sm mt-1">{b.service_name} · {b.date} {b.start_time}</div>
                    {b.counselor_name && (
                      <div className="text-xs mt-1" style={{color:"var(--brand)"}}>with {b.counselor_name}</div>
                    )}
                    {b.booked_for === "guardian" && b.student_name && (
                      <div className="text-xs mt-1" style={{color:"var(--brand)"}}>
                        Guardian booking · For {b.student_name}{b.student_age ? ` (age ${b.student_age})` : ""}
                      </div>
                    )}
                    {b.notes && (
                      <div className="text-xs mt-1 italic" style={{color:"var(--text-secondary)"}}>“{b.notes}”</div>
                    )}
                    {b.status === "completed" && b.feedback_submitted && (
                      <div className="mt-2">
                        <FeedbackViewer bookingId={b.id} testIdPrefix="counselor-feedback" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="chip" style={{background: b.payment_status==="paid" ? "rgba(91,117,83,0.15)":"rgba(232,176,92,0.2)", color: b.payment_status==="paid"?"var(--brand)":"#8a6a1f"}}>
                      {b.payment_status}
                    </span>
                    <span className="chip">{b.status.replace("_"," ")}</span>
                    {b.status === "confirmed" && (
                      <>
                        <RescheduleControls booking={b} viewerRole="counselor"
                          testIdPrefix={`counselor-b-${b.id}-`}
                          onChange={(updated) => setBookings(prev => prev.map(x => x.id === updated.id ? updated : x))}/>
                        <button data-testid={`complete-${b.id}`} onClick={()=>updateStatus(b.id,"completed")} className="btn-outline text-sm inline-flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4"/> Complete
                        </button>
                        <button data-testid={`cancel-${b.id}`} onClick={()=>updateStatus(b.id,"cancelled")} className="btn-outline text-sm inline-flex items-center gap-1">
                          <XCircle className="w-4 h-4"/> Cancel
                        </button>
                        {isLead && b.razorpay_payment_id && (
                          <button data-testid={`refund-${b.id}`} onClick={()=>refundBooking(b)}
                            className="btn-outline text-sm inline-flex items-center gap-1"
                            style={{borderColor:"var(--accent)", color:"var(--accent-hover)"}}>
                            <IndianRupee className="w-4 h-4"/> Refund
                          </button>
                        )}
                      </>
                    )}
                    {b.status === "refunded" && (
                      <span className="chip text-xs" data-testid={`refunded-badge-${b.id}`}
                            style={{background:"rgba(230,178,58,0.18)", color:"#8a6a1f"}}>
                        Refunded {b.refund_amount ? `· ₹${b.refund_amount}` : ""}
                      </span>
                    )}
                    {isLead && (
                      <>
                        {b.archived && (
                          <span className="chip text-[0.65rem]" style={{background:"rgba(0,0,0,0.06)", color:"var(--text-secondary)"}}>archived</span>
                        )}
                        {!b.archived ? (
                          <button data-testid={`archive-${b.id}`} onClick={()=>archiveBooking(b.id)}
                            disabled={rowBusy === b.id}
                            className="btn-outline text-xs inline-flex items-center gap-1 disabled:opacity-50">
                            <Archive className="w-3.5 h-3.5"/> Archive
                          </button>
                        ) : (
                          <button data-testid={`unarchive-${b.id}`} onClick={()=>unarchiveBooking(b.id)}
                            disabled={rowBusy === b.id}
                            className="btn-outline text-xs inline-flex items-center gap-1 disabled:opacity-50">
                            <ArchiveRestore className="w-3.5 h-3.5"/> Restore
                          </button>
                        )}
                        <button data-testid={`delete-booking-${b.id}`} onClick={()=>deleteBooking(b.id)}
                          disabled={rowBusy === b.id}
                          className="btn-outline text-xs inline-flex items-center gap-1 disabled:opacity-50"
                          style={{color:"var(--accent-hover)"}}>
                          <Trash2 className="w-3.5 h-3.5"/> Delete
                        </button>
                        <PowerDeleteButton collection="bookings" id={b.id} onDeleted={load} />
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {isLead && (
        <>
          {showSection("people") && (
          <div className="card-soft p-8 mb-8" id="apps-section">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-serif-display text-2xl inline-flex items-center gap-2"><Sparkles className="w-5 h-5" style={{color:"var(--accent)"}}/> Counselor applications</h2>
              <span className="text-xs" style={{color:"var(--text-secondary)"}}>{applications.filter(a => a.status === "pending").length} pending</span>
            </div>
            {applications.length === 0 ? (
              <p style={{color:"var(--text-secondary)"}}>No applications yet. Share <code>/apply</code> with prospective counselors.</p>
            ) : (
              <div className="space-y-3" data-testid="applications-list">
                {applications.map(a => (
                  <div key={a.id} data-testid={`app-${a.id}`}
                    className="border border-[color:var(--border-soft)] rounded-2xl p-4 flex flex-wrap gap-3 justify-between items-start">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-serif-display text-lg">{a.name}</div>
                        <span className="chip text-[0.6rem]" style={{
                          background: a.status === "approved" ? "rgba(91,117,83,0.15)"
                                    : a.status === "rejected" ? "rgba(230,178,58,0.15)" : "rgba(232,176,92,0.2)",
                          color: a.status === "approved" ? "var(--brand)"
                               : a.status === "rejected" ? "var(--accent)" : "#8a6a1f"
                        }}>{a.status}</span>
                        <PowerDeleteButton collection="counselor_applications" id={a.id} onDeleted={load} />
                      </div>
                      <div className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>
                        {a.email} · {a.experience_months ? (a.experience_months >= 12 ? Math.floor(a.experience_months/12)+"y"+(a.experience_months%12?` ${a.experience_months%12}m`:"") : a.experience_months+"mo") : "—"} · {(a.languages||[]).join("/")}
                      </div>
                      {a.rci_license && <div className="text-xs mt-1" style={{color:"var(--brand)"}}>RCI: {a.rci_license}</div>}
                      {a.qualification_path && (
                        <div className="text-xs mt-1 font-medium" style={{color:"var(--brand)"}}>
                          Qualification: {QUALIFICATION_LABELS[a.qualification_path] || a.qualification_path}
                        </div>
                      )}
                      {(a.qualification_certificate_urls || []).map((u, i) => (
                        <a key={u} href={u} target="_blank" rel="noreferrer" className="text-xs mr-3 underline" style={{color:"var(--brand)"}}>Certificate {i + 1} ↗</a>
                      ))}
                      {a.photo_url && <a href={a.photo_url} target="_blank" rel="noreferrer" className="text-xs mr-3 underline" style={{color:"var(--brand)"}}>Photo ↗</a>}
                      {a.cv_url && <a href={a.cv_url} target="_blank" rel="noreferrer" className="text-xs mr-3 underline" style={{color:"var(--brand)"}}>CV ↗</a>}
                      {a.intro_video_url && <a href={a.intro_video_url} target="_blank" rel="noreferrer" className="text-xs mr-3 underline" style={{color:"var(--brand)"}}>Video ↗</a>}
                      <div className="text-sm mt-1" style={{color:"var(--brand)"}}>{a.specialty}</div>
                      <div className="text-sm mt-1 italic" style={{color:"var(--text-secondary)"}}>"{a.bio}"</div>
                      {a.credentials && <div className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>Credentials: {a.credentials}</div>}
                      {a.other_qualification_details && (
                        <div className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>Other qualification details: "{a.other_qualification_details}"</div>
                      )}
                    </div>
                    {a.status === "pending" && (
                      <div className="flex gap-2">
                        <button data-testid={`approve-${a.id}`} onClick={()=>decideApp(a, "approve")} className="btn-primary text-sm">
                          <ThumbsUp className="w-4 h-4"/> Approve
                        </button>
                        <button data-testid={`reject-${a.id}`} onClick={()=>decideApp(a, "reject")} className="btn-outline text-sm">
                          <ThumbsDown className="w-4 h-4"/> Reject
                        </button>
                      </div>
                    )}
                    {a.status === "approved" && (
                      <div className="text-xs rounded px-2 py-1"
                        style={{background:"rgba(91,117,83,0.1)", color:"var(--brand)"}}>
                        ✓ Approved · counsellor picks a tier (PNB free or EEA paid) on sign-in
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {showSection("people") && <AdminProfileEditsPanel />}

          {showSection("people") && (
          <div className="card-soft p-8 mb-8" id="kyc-review-section">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-serif-display text-2xl inline-flex items-center gap-2">
                <IdCard className="w-5 h-5" style={{ color: "var(--brand)" }} /> Counsellor KYC & payouts
              </h2>
              <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                {kycQueue.filter(k => k.status === "pending_review").length} pending
              </span>
            </div>
            {kycQueue.length === 0 ? (
              <p style={{ color: "var(--text-secondary)" }}>
                No KYC submissions yet. Counsellors submit these after picking a membership tier.
              </p>
            ) : (
              <div className="space-y-3" data-testid="kyc-queue">
                {kycQueue.map(k => (
                  <div key={k.user_id} data-testid={`kyc-${k.user_id}`}
                    className="border border-[color:var(--border-soft)] rounded-2xl p-4 flex flex-wrap gap-3 justify-between items-start">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-serif-display text-lg">{k.full_name}</div>
                        <span className="chip text-[0.6rem]" style={{
                          background: k.status === "verified" ? "rgba(91,117,83,0.15)"
                                    : k.status === "rejected" ? "rgba(230,178,58,0.15)"
                                    : "rgba(232,176,92,0.2)",
                          color: k.status === "verified" ? "var(--brand)"
                               : k.status === "rejected" ? "var(--accent)" : "#8a6a1f",
                        }}>{k.status.replace("_", " ")}</span>
                        {k.razorpay_linked_account_id && (
                          <span className="chip text-[0.6rem]" style={{ background: "rgba(91,117,83,0.08)", color: "var(--brand)" }}>
                            Route · <code>{k.razorpay_linked_account_id}</code>
                          </span>
                        )}
                      </div>
                      <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                        {k.email} · PAN {k.pan} · Bank {k.bank_holder_name} · A/C {k.bank_account_number} · IFSC {k.bank_ifsc}
                      </div>
                      <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                        {k.address_line1}{k.address_line2 ? `, ${k.address_line2}` : ""}, {k.city}, {k.state} {k.pincode} · DOB {k.dob} · Phone {k.phone}
                      </div>
                      {k.razorpay_error && (
                        <div className="text-xs mt-1 flex items-center gap-1" style={{ color: "var(--accent)" }}>
                          <AlertTriangle className="w-3 h-3" /> Razorpay: {k.razorpay_error}
                        </div>
                      )}
                      {k.rejection_reason && (
                        <div className="text-xs mt-1" style={{ color: "var(--accent)" }}>Reason: {k.rejection_reason}</div>
                      )}
                    </div>
                    {k.status === "pending_review" && (
                      <div className="flex gap-2">
                        <button data-testid={`kyc-approve-${k.user_id}`}
                          disabled={kycBusyId === k.user_id}
                          onClick={() => reviewKyc(k, "approve")}
                          className="btn-primary text-sm">
                          <ThumbsUp className="w-4 h-4" /> Verify
                        </button>
                        <button data-testid={`kyc-reject-${k.user_id}`}
                          disabled={kycBusyId === k.user_id}
                          onClick={() => reviewKyc(k, "reject")}
                          className="btn-outline text-sm">
                          <ThumbsDown className="w-4 h-4" /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          )}


          {showSection("payments") && payments && (
            <>
            <AdminServiceDiscountPanel />
            <div className="card-soft p-8 mb-8" id="payments-section">
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-serif-display text-2xl inline-flex items-center gap-2"><Wallet className="w-5 h-5" style={{color:"var(--brand)"}}/> Payments</h2>
                <span className="text-xs" style={{color:"var(--text-secondary)"}}>{payments.summary.count_total} transactions</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="revenue-summary">
                <div className="rounded-2xl p-4" style={{background:"rgba(91,117,83,0.05)"}}>
                  <div className="label-eyebrow">Appointment revenue</div>
                  <div className="font-serif-display text-2xl mt-1">₹{payments.summary.revenue_appointment.toLocaleString("en-IN")}</div>
                </div>
                <div className="rounded-2xl p-4" style={{background:"rgba(91,117,83,0.05)"}}>
                  <div className="label-eyebrow">Membership revenue</div>
                  <div className="font-serif-display text-2xl mt-1">₹{payments.summary.revenue_membership.toLocaleString("en-IN")}</div>
                </div>
                <div className="rounded-2xl p-4" style={{background:"rgba(91,117,83,0.05)"}}>
                  <div className="label-eyebrow">Counsellors' share (paid)</div>
                  <div className="font-serif-display text-2xl mt-1">₹{(payments.summary.counselor_share_appointment ?? 0).toLocaleString("en-IN")}</div>
                </div>
                <div className="rounded-2xl p-4" style={{background:"rgba(230,178,58,0.06)"}}>
                  <div className="label-eyebrow">Platform net</div>
                  <div className="font-serif-display text-2xl mt-1">₹{(payments.summary.platform_net_total ?? payments.summary.revenue_total).toLocaleString("en-IN")}</div>
                  <div className="text-[0.65rem] mt-1" style={{color:"var(--text-secondary)"}}>Membership + 30% from PNB bookings</div>
                </div>
              </div>
              <div className="text-xs mb-3" style={{color:"var(--text-secondary)"}}>
                <span className="mr-3">Paid: {payments.summary.count_paid}</span>
                <span className="mr-3">Pending: {payments.summary.count_pending}</span>
                <span>Failed: {payments.summary.count_failed}</span>
              </div>
              {payments.payments.length === 0 ? (
                <p className="text-sm" style={{color:"var(--text-secondary)"}}>No transactions yet.</p>
              ) : (
                <div className="overflow-x-auto" data-testid="payments-table">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-widest" style={{color:"var(--text-secondary)"}}>
                      <tr className="border-b border-[color:var(--border-soft)]">
                        <th className="text-left py-2 pr-3">When</th>
                        <th className="text-left py-2 pr-3">Type</th>
                        <th className="text-left py-2 pr-3">User</th>
                        <th className="text-left py-2 pr-3">Amount</th>
                        <th className="text-left py-2 pr-3">Status</th>
                        <th className="text-left py-2 pr-3">Ref</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.payments.slice(0, 50).map(p => (
                        <tr key={p.id} className="border-b border-[color:var(--border-soft)]/60">
                          <td className="py-2 pr-3 whitespace-nowrap">{new Date(p.created_at).toLocaleString()}</td>
                          <td className="py-2 pr-3 capitalize">{p.payment_type}</td>
                          <td className="py-2 pr-3">
                            <div>{p.user_name || "—"}</div>
                            <div className="text-xs" style={{color:"var(--text-secondary)"}}>{p.user_email || ""}</div>
                          </td>
                          <td className="py-2 pr-3">₹{Math.round(p.amount || 0).toLocaleString("en-IN")}</td>
                          <td className="py-2 pr-3">
                            <span className="chip text-[0.6rem]" style={{
                              background: p.payment_status === "paid" ? "rgba(91,117,83,0.15)"
                                        : p.payment_status === "failed" ? "rgba(230,178,58,0.15)" : "rgba(232,176,92,0.2)",
                              color: p.payment_status === "paid" ? "var(--brand)"
                                   : p.payment_status === "failed" ? "var(--accent)" : "#8a6a1f"
                            }}>{p.payment_status}</span>
                          </td>
                          <td className="py-2 pr-3 text-xs" style={{color:"var(--text-secondary)"}}>
                            <code>{(p.razorpay_payment_id || p.razorpay_order_id || "—").slice(0, 22)}</code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </>
          )}

          {showSection("people") && (
          <div className="card-soft p-8 mb-8" id="tickets-section">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-serif-display text-2xl inline-flex items-center gap-2"><MailOpen className="w-5 h-5" style={{color:"var(--brand)"}}/> Escalations from Mooly</h2>
              <span className="text-xs" style={{color:"var(--text-secondary)"}}>{tickets.filter(t => t.status === "open").length} open</span>
            </div>
            {tickets.length === 0 ? (
              <p style={{color:"var(--text-secondary)"}}>No escalations yet. Mooly is handling visitor questions on the site.</p>
            ) : (
              <div className="space-y-3" data-testid="tickets-list">
                {tickets.map(t => (
                  <div key={t.id} data-testid={`ticket-${t.id}`}
                    className="border border-[color:var(--border-soft)] rounded-2xl p-4 flex flex-wrap gap-3 justify-between items-start">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-serif-display text-lg">{t.subject}</div>
                        <span className="chip text-[0.6rem]" style={{
                          background: t.status === "open" ? "rgba(232,176,92,0.2)" : "rgba(91,117,83,0.15)",
                          color: t.status === "open" ? "#8a6a1f" : "var(--brand)"
                        }}>{t.status}</span>
                        <PowerDeleteButton collection="support_tickets" id={t.id} onDeleted={load} />
                      </div>
                      <div className="text-xs mt-1" style={{color:"var(--text-secondary)"}}>
                        {t.visitor_name || "Anonymous"} · {t.visitor_email || "no email"} · {new Date(t.created_at).toLocaleString()}
                      </div>
                      {t.summary && (
                        <details className="mt-2 text-sm">
                          <summary className="cursor-pointer" style={{color:"var(--brand)"}}>View transcript summary</summary>
                          <pre className="text-xs mt-2 whitespace-pre-wrap font-sans-body"
                            style={{color:"var(--text-secondary)"}}>{t.summary}</pre>
                        </details>
                      )}
                    </div>
                    {t.status === "open" && (
                      <button data-testid={`close-ticket-${t.id}`} onClick={()=>closeTicket(t.id)} className="btn-outline text-sm">
                        Mark closed
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {showSection("people") && (
          <div className="card-soft p-8 mb-8" id="users-section">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-serif-display text-2xl inline-flex items-center gap-2"><Users className="w-5 h-5" style={{color:"var(--brand)"}}/> Manage users</h2>
              <span className="text-xs" style={{color:"var(--text-secondary)"}}>{manageableUsers.filter(u => !u.is_lead).length} accounts</span>
            </div>
            {manageableUsers.length === 0 ? (
              <p style={{color:"var(--text-secondary)"}}>No users yet.</p>
            ) : (
              <div className="space-y-2" data-testid="users-list">
                {manageableUsers.map(u => (
                  <div key={u.id} data-testid={`user-${u.id}`}
                       className="border border-[color:var(--border-soft)] rounded-2xl p-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{u.name}</span>
                        <span className="chip text-[0.6rem]" style={{
                          background: u.role === "counselor" ? "rgba(91,117,83,0.15)" : "rgba(232,176,92,0.2)",
                          color: u.role === "counselor" ? "var(--brand)" : "#8a6a1f"
                        }}>{u.is_lead ? "lead admin" : u.role}</span>
                        {u.membership_status && (
                          <span className="chip text-[0.55rem]" style={{background: "rgba(0,0,0,0.05)", color: "var(--text-secondary)"}}>{u.membership_status}</span>
                        )}
                        {u.admin_generated && (
                          <span className="chip text-[0.55rem]" data-testid={`admin-generated-tag-${u.id}`}
                                style={{background: "rgba(230,178,58,0.18)", color: "#8a6a1f"}}>Admin-generated</span>
                        )}
                        {isCounselorDisabled(u) && (
                          <span data-testid={`disabled-badge-${u.id}`}
                                className="chip text-[0.55rem] inline-flex items-center gap-0.5"
                                style={{background:"rgba(0,0,0,0.05)", color:"var(--accent-hover)"}}>
                            <Ban className="w-2.5 h-2.5"/> disabled · until {new Date(u.disabled_until).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <div className="text-xs" style={{color:"var(--text-secondary)"}}>{u.email} · joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</div>
                      {u.role === "client" && (clientCoupons[u.id] || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`client-coupons-${u.id}`}>
                          {clientCoupons[u.id].map(cc => (
                            <span key={cc.id} className="chip text-[0.6rem] inline-flex items-center gap-1"
                              style={cc.expired
                                ? {background:"rgba(0,0,0,0.05)", color:"var(--text-secondary)", textDecoration:"line-through"}
                                : {background:"rgba(91,117,83,0.15)", color:"var(--brand)"}}>
                              <Ticket className="w-2.5 h-2.5"/> {cc.service_name}: {cc.code}
                              {cc.expired ? " (expired)" : (cc.expires_at ? ` · exp ${new Date(cc.expires_at).toLocaleDateString()}` : "")}
                              <PowerDeleteButton collection="client_coupons" id={cc.id} onDeleted={load} />
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    {u.is_lead ? (
                      <span className="text-xs" style={{color:"var(--text-secondary)"}}>Protected</span>
                    ) : (
                      <div className="flex gap-2 flex-wrap items-center">
                        {u.role === "client" && (
                          <div className="flex items-center gap-1.5">
                            <input data-testid={`attach-coupon-input-${u.id}`} placeholder="Paste coupon code"
                              value={couponInputs[u.id] || ""}
                              onChange={e => setCouponInputs(prev => ({ ...prev, [u.id]: e.target.value }))}
                              className="w-36 text-xs rounded-lg border border-[color:var(--border-soft)] px-2 py-1.5 focus:outline-none focus:border-[color:var(--brand)]" />
                            <button data-testid={`attach-coupon-btn-${u.id}`} onClick={() => attachCoupon(u)}
                              disabled={couponBusyId === u.id}
                              className="btn-outline text-xs inline-flex items-center gap-1 disabled:opacity-40">
                              <Ticket className="w-3.5 h-3.5"/> {couponBusyId === u.id ? "Attaching…" : "Attach coupon"}
                            </button>
                          </div>
                        )}
                        {u.role === "counselor" && (
                          isCounselorDisabled(u) ? (
                            <button data-testid={`admin-enable-${u.id}`}
                                    onClick={()=>enableCounselor(u)}
                                    className="btn-outline text-sm inline-flex items-center gap-1">
                              <RotateCcw className="w-4 h-4"/> Enable
                            </button>
                          ) : (
                            <select data-testid={`admin-disable-select-${u.id}`}
                                    defaultValue=""
                                    onChange={e => {
                                      const v = e.target.value;
                                      if (v) { disableCounselor(u, v); e.target.value = ""; }
                                    }}
                                    className="text-sm rounded-lg border border-[color:var(--border-soft)] px-2 py-1.5 bg-white">
                              <option value="" disabled>Disable…</option>
                              <option value="1w">1 week</option>
                              <option value="1m">1 month</option>
                              <option value="1y">1 year</option>
                            </select>
                          )
                        )}
                        <button data-testid={`impersonate-${u.id}`}
                                onClick={()=>impersonateUser(u)}
                                className="btn-outline text-sm inline-flex items-center gap-1">
                          <LogIn className="w-4 h-4"/> View as
                        </button>
                        <button data-testid={`delete-user-${u.id}`} onClick={()=>deleteUser(u)}
                                className="btn-outline text-sm inline-flex items-center gap-1"
                                style={{borderColor:"var(--accent)", color:"var(--accent)"}}>
                          <Trash2 className="w-4 h-4"/> Delete
                        </button>
                        <PowerDeleteButton collection="users" id={u.id} onDeleted={load} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {showSection("employees") && <EmployeeAdminPanel />}

          {showSection("brand") && <AdminBrandProfilePanel />}

          <DeletedAccountsBin />
          <PowerDeletedBin />
        </>
      )}

      {!isLead && <DeleteAccountCard/>}
      {showEditProfile && (
        <EditProfileModal user={user} onClose={() => setShowEditProfile(false)}
          onSaved={(data) => { if (data.applied) { refresh(); } else { load(); } }} />
      )}
    </div>
  );
}
